import { Button, LoadSpinner } from '@calizahq/react-components-v2'
import { WorkspaceAction } from '@sales-app/context/workspace/workspace.actions'
import { useWorkspaceContext } from '@sales-app/context/workspace/workspace.hooks'
import { WorkspaceView } from '@sales-app/context/workspace/workspace.type'
import { getWorkspacePath } from '@sales-app/context/workspace/workspace.util'
import { PeopleQueries } from '@sales-app/graphql/queries/people.queries'
import { PotentialBookingQueries } from '@sales-app/graphql/queries/potentialBooking/potentialBookings.queries'
import { BookablePersonType } from '@sales-app/types/BookablePersonType'
import { PersonsReservationRow } from '@sales-app/types/Person'
import { BottomBar } from '@sales-app/views/BottomBar/BottomBar'
import { BookablePersonHeader } from '@sales-app/views/Shared/BookablePersonHeader'
import clsx from 'clsx'
import React, { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { NoResults, Table } from '@/components'
import { useUserContext } from '@/context'
import { AgentRoles } from '@/context/user/user.types'
import { useGraphql } from '@/hooks/useGraphql'
import { formatDate, parseDate } from '@/util'

import { WorkspaceTabs } from '../WorkspaceNavigation/WorkspaceTabs'
import { CommunicationLogs } from './CommunicationLogs/CommunicationLogs'
import { DuplicatePersons } from './DuplicatePersons/DuplicatePersons'
import { PotentialBookingColumns } from './PersonSummary.data'

export const PersonSummary = () => {
  const { user } = useUserContext()
  const { bookablePersonId = '' } = useParams<{ bookablePersonId: string }>()
  const { bookablePersonType } = useParams<{ bookablePersonType: string }>()
  const allowDuplicateReview = user?.agent?.role === AgentRoles.SUPERVISOR
  const navigate = useNavigate()
  const [{ tabs, activeTabId, activeTabType }, workspaceDispatch] = useWorkspaceContext()

  const ADMIN_URL = process.env.ADMIN_URL

  const activeWorkspaceTab =
    activeTabId && activeTabType
      ? tabs.find((t) => t.bookablePersonId === activeTabId && t.bookablePersonType === activeTabType)
      : undefined

  const bookablePersonTypeEnum = useMemo(() => {
    return bookablePersonType === 'member' ? BookablePersonType.MEMBER : BookablePersonType.LEAD
  }, [bookablePersonType])

  const bookablePersonIdNumber = useMemo(() => {
    return Number(bookablePersonId)
  }, [bookablePersonId])

  const {
    data: bookablePersonData,
    isLoading: isBookablePersonLoading,
    refetch,
  } = useGraphql<PeopleQueries.GET_BOOKABLE_PERSON.Response>(
    PeopleQueries.getBookablePerson.name,
    PeopleQueries.getBookablePerson(),
    {
      bookablePersonRecord: {
        bookablePersonId: bookablePersonIdNumber,
        bookablePersonType: bookablePersonTypeEnum,
      },
    },
    { refetchOnMount: 'always' },
  )

  const { data, isLoading } = useGraphql<PotentialBookingQueries.GET_POTENTIAL_BOOKINGS_BY_BOOKABLE_PERSON.Response>(
    PotentialBookingQueries.getPotentialBookingsByBookablePerson.name,
    PotentialBookingQueries.getPotentialBookingsByBookablePerson(),
    {
      bookablePersonId: bookablePersonIdNumber,
      bookablePersonType: bookablePersonTypeEnum,
    },
    { refetchOnMount: 'always' },
  )

  const { data: duplicatesData, isLoading: isDuplicatesLoading } =
    useGraphql<PeopleQueries.GET_BOOKABLE_PERSON_DUPLICATES.Response>(
      PeopleQueries.getBookablePersonDuplicates.name,
      PeopleQueries.getBookablePersonDuplicates(),
      {
        bookablePersonRecord: {
          bookablePersonId: bookablePersonIdNumber,
          bookablePersonType: bookablePersonTypeEnum,
        },
      },
    )

  useEffect(() => {
    if (bookablePersonData?.bookablePerson) {
      if (
        bookablePersonData?.bookablePerson.bookablePersonId !== activeWorkspaceTab?.bookablePersonId ||
        bookablePersonData?.bookablePerson.bookablePersonType !== activeWorkspaceTab?.bookablePersonType
      ) {
        workspaceDispatch(
          WorkspaceAction.openTab({
            bookablePersonId: bookablePersonData?.bookablePerson.bookablePersonId,
            bookablePersonType: bookablePersonData?.bookablePerson.bookablePersonType,
            label: bookablePersonData?.bookablePerson.fullName + '',
            lastAccessedTime: new Date(),
            potentialBookingId: null,
            potentialBooking: null,
            view: WorkspaceView.SUMMARY,
            potentialBookingFilters: null,
            userId: bookablePersonData?.bookablePerson.userId,
          }),
        )
      } else if (activeWorkspaceTab) {
        if (activeWorkspaceTab.view !== WorkspaceView.SUMMARY) {
          workspaceDispatch(
            WorkspaceAction.updateTab({
              ...activeWorkspaceTab,
              view: WorkspaceView.SUMMARY,
              potentialBooking: null,
              potentialBookingId: null,
              label: bookablePersonData?.bookablePerson.fullName + '',
              userId: bookablePersonData?.bookablePerson.userId,
            }),
          )
        }
      }
    }
  }, [bookablePersonData, activeWorkspaceTab, workspaceDispatch])

  const potentialBookingData = data?.bookablePersonPotentialBookings ? data.bookablePersonPotentialBookings : []

  const newPotentialBookingView = () => {
    if (activeWorkspaceTab) {
      const { bookablePersonId, bookablePersonType } = activeWorkspaceTab
      workspaceDispatch(
        WorkspaceAction.updateTab({
          ...activeWorkspaceTab,
          view: WorkspaceView.POTENTIALBOOKING,
          potentialBooking: null,
          potentialBookingId: null,
          potentialBookingFilters: null,
        }),
      )
      const path = getWorkspacePath({
        view: WorkspaceView.POTENTIALBOOKING,
        type: bookablePersonType,
        id: bookablePersonId,
        potentialBookingId: null,
      })

      navigate(path)
    }
  }

  const potentialBookingsColumns = useMemo(() => {
    return PotentialBookingColumns
  }, [])

  const reservations = useMemo(() => {
    return bookablePersonData?.bookablePerson?.reservations ? bookablePersonData.bookablePerson.reservations : []
  }, [bookablePersonData])

  return (
    <div className="flex w-full flex-col p-4 md:p-8">
      {tabs && tabs.length > 0 ? (
        <>
          <WorkspaceTabs />
          <div className="flex h-full w-full">
            <>
              {isLoading || isBookablePersonLoading ? (
                <div className="flex h-20 w-full items-center justify-center">
                  <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
                </div>
              ) : (
                <>
                  {bookablePersonData && bookablePersonData.bookablePerson && (
                    <div className="flex w-full flex-col">
                      <BookablePersonHeader
                        bookablePerson={bookablePersonData.bookablePerson}
                        hasDuplicates={!!duplicatesData?.bookablePerson?.duplicatesForReview?.length}
                        isDuplicatesLoading={isDuplicatesLoading}
                        refetch={refetch}
                      />
                      <div className="flex w-full">
                        <div className="w-full">
                          <div className="mb-6 flex w-full flex-col">
                            <div className="mb-4 flex items-center justify-between">
                              <h3 className=" text-header-md">Potential Bookings</h3>
                              <div>
                                <Button
                                  className=" bg-text-semantic-success"
                                  onClick={() => newPotentialBookingView()}
                                  size="small"
                                >
                                  Create
                                </Button>
                              </div>
                            </div>
                            <Table columns={potentialBookingsColumns as any} data={potentialBookingData} />
                          </div>
                          {allowDuplicateReview && activeWorkspaceTab && (
                            <DuplicatePersons bookablePerson={activeWorkspaceTab} />
                          )}

                          <div className="flex w-full">
                            <div
                              className={`flex flex-col mb-6 mr-4 ${
                                reservations && reservations.length > 0 ? 'w-2/3' : 'w-full'
                              }`}
                            >
                              <h3 className="mb-4 text-header-md">Activity Feed</h3>
                              <CommunicationLogs bookablePerson={bookablePersonData.bookablePerson} />
                            </div>
                            {reservations && reservations.length > 0 && (
                              <div className="flex w-1/3">
                                <div className="mb-6 flex w-full flex-col">
                                  <h3 className="mb-4 text-header-md">Reservation History</h3>
                                  <SectionContainer className="flex flex-col max-h-[70vh] overflow-y-auto">
                                    {reservations.map((reservation: PersonsReservationRow) => {
                                      return (
                                        <div className="pb-4" key={reservation.id}>
                                          <div className="text-header-xs font-bold pb-1">
                                            {reservation.home.searchDocument}
                                          </div>
                                          <div className="text-tertiary pb-1">
                                            {reservation.home.property.market.name}{' '}
                                            {reservation.home.property.marketSegment}
                                          </div>
                                          <div className="text-tertiary underline">
                                            <a
                                              href={`${ADMIN_URL}/admin/reservations/${reservation.id}`}
                                              rel="noreferrer"
                                              target="_blank"
                                            >
                                              {`${formatDate(parseDate(reservation.moveInDate), 'MMM d, Y')} - `}
                                              {reservation.moveOutDate
                                                ? `${formatDate(parseDate(reservation.moveOutDate), 'MMM d, Y')}`
                                                : 'Pending'}{' '}
                                            </a>
                                          </div>
                                        </div>
                                      )
                                    })}
                                  </SectionContainer>
                                </div>
                              </div>
                            )}{' '}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          </div>
        </>
      ) : (
        <NoResults />
      )}
      <BottomBar />
    </div>
  )
}

export const SectionContainer = ({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) => (
  <div {...props} className={clsx('md:border-secondary rounded-2xl md:px-4 md:py-4 md:border', className)} />
)
